import React, { useState, useMemo, useCallback } from 'react';
import { X, AlertTriangle, CheckCircle2, FileText, Info, Trash2 } from 'lucide-react';
import { apiService } from '../../services/apiService';

interface AIModel {
  ranked_position?: number;
  id?: string;
  [key: string]: any;
}

interface ParsedModel {
  name: string;
  providerModelId: string;
  customEndpointUrl: string;
  customApiKey: string;
  contextWindow: number;
  maxTokens: number;
  supports_vision: boolean;
  supports_streaming: boolean;
  taskDeterminationModel: boolean;
  isPrivacyCompliant: boolean;
  functionCalling: boolean;
  isUncensored: boolean;
  category: string;
  ranked_position: number | undefined;
  price_per_token: string;
  capabilities: Record<string, number>;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onImportComplete: (imported: number, errors: { row: number; message: string }[]) => void;
  existingModels: AIModel[];
  isAdmin: boolean;
}

function parseCSVText(text: string): { rows: Record<string, string>[]; error?: string } {
  if (text.charCodeAt(0) === 0xFEFF) text = text.slice(1);

  const allLines = text.split(/\r?\n/);
  const cleaned = allLines.filter(line => {
    const t = line.trim();
    if (!t) return false;
    if (t.startsWith('#') || t.startsWith('"#')) return false;
    if (t.toLowerCase().startsWith('sep=')) return false;
    const withoutTrailing = t.replace(/[,;]+$/, '');
    if (withoutTrailing.startsWith('#') || withoutTrailing.startsWith('"#')) return false;
    return true;
  });

  if (cleaned.length < 2) return { rows: [], error: 'CSV has no data rows' };

  const header = cleaned[0];
  const sep = (header.match(/;/g) || []).length > (header.match(/,/g) || []).length ? ';' : ',';
  const headers = header.split(sep).map(h => h.replace(/^\uFEFF/, '').trim().replace(/^"|"$/g, ''));

  if (!headers.some(h => h.toLowerCase() === 'name')) {
    return { rows: [], error: `Could not find "Name" column. Detected columns: ${headers.slice(0, 5).join(', ')}` };
  }

  const rows: Record<string, string>[] = [];
  for (let i = 1; i < cleaned.length; i++) {
    const cells = cleaned[i].split(sep).map(c => c.trim().replace(/^"|"$/g, ''));
    if (cells.every(c => !c)) continue;
    const row: Record<string, string> = {};
    headers.forEach((h, j) => { row[h] = cells[j] ?? ''; });
    rows.push(row);
  }
  return { rows };
}

function getCol(row: Record<string, string>, col: string): string {
  if (row[col] !== undefined) return row[col];
  const lower = col.toLowerCase();
  for (const k of Object.keys(row)) {
    if (k.toLowerCase() === lower) return row[k];
  }
  return '';
}

function parseBool(val: string): boolean {
  return ['yes', 'true', '1'].includes(val.trim().toLowerCase());
}

function parseNum(val: string, def: number): number {
  if (!val || val.trim() === '') return def;
  const n = parseInt(val.trim(), 10);
  return isNaN(n) ? def : n;
}

function rowToModel(row: Record<string, string>): ParsedModel {
  const rankStr = getCol(row, 'Ranked Position').trim();
  const streamingVal = getCol(row, 'Supports Streaming');
  return {
    name: getCol(row, 'Name').trim(),
    providerModelId: getCol(row, 'Model ID').trim(),
    customEndpointUrl: getCol(row, 'Endpoint URL').trim(),
    customApiKey: getCol(row, 'API Key').trim(),
    contextWindow: parseNum(getCol(row, 'Context Window'), 32000),
    maxTokens: parseNum(getCol(row, 'Max Tokens'), 4096),
    supports_vision: parseBool(getCol(row, 'Supports Vision')),
    supports_streaming: streamingVal ? parseBool(streamingVal) : true,
    taskDeterminationModel: parseBool(getCol(row, 'Task Determination')),
    isPrivacyCompliant: parseBool(getCol(row, 'Privacy Compliant')),
    functionCalling: parseBool(getCol(row, 'Function Calling')),
    isUncensored: parseBool(getCol(row, 'Uncensored')),
    category: getCol(row, 'Category').trim(),
    ranked_position: rankStr ? parseNum(rankStr, 100) : undefined,
    price_per_token: getCol(row, 'Pricing').trim() || 'Free',
    capabilities: {
      creativity: parseNum(getCol(row, 'Creativity'), 3),
      reasoning: parseNum(getCol(row, 'Reasoning'), 3),
      speed: parseNum(getCol(row, 'Speed'), 3),
      coding: parseNum(getCol(row, 'Coding'), 3),
      webSearch: parseNum(getCol(row, 'WebSearch'), 0),
      enhancedReasoning: parseNum(getCol(row, 'EnhancedReasoning'), 0),
      imageGeneration: parseNum(getCol(row, 'ImageGeneration'), 0),
      conversational: parseNum(getCol(row, 'Conversational'), 3),
    },
  };
}

export default function CSVImportWizard({ isOpen, onClose, onImportComplete, existingModels, isAdmin }: Props) {
  const [parsedModels, setParsedModels] = useState<ParsedModel[]>([]);
  const [parseError, setParseError] = useState('');
  const [hasParsed, setHasParsed] = useState(false);
  const [importing, setImporting] = useState(false);

  const existingRanks = useMemo(
    () => new Set(existingModels.map(m => m.ranked_position).filter((r): r is number => r != null)),
    [existingModels]
  );

  // Map of row index -> conflict type ('existing' = taken by a loaded model, 'duplicate' = appears twice in this CSV)
  const conflicts = useMemo(() => {
    const result = new Map<number, 'existing' | 'duplicate'>();
    const seenInBatch = new Map<number, number>(); // rank -> first row index

    parsedModels.forEach((model, i) => {
      const rank = model.ranked_position;
      if (rank == null) return;
      if (existingRanks.has(rank)) {
        result.set(i, 'existing');
      } else if (seenInBatch.has(rank)) {
        result.set(i, 'duplicate');
        result.set(seenInBatch.get(rank)!, 'duplicate');
      } else {
        seenInBatch.set(rank, i);
      }
    });
    return result;
  }, [parsedModels, existingRanks]);

  const missingFields = useMemo(() => {
    const result = new Set<number>();
    parsedModels.forEach((m, i) => {
      if (!m.name || !m.providerModelId || !m.customEndpointUrl) result.add(i);
    });
    return result;
  }, [parsedModels]);

  const existingModelKeys = useMemo(() => {
    return new Set(
      existingModels
        .filter(m => m.providerModelId && m.customEndpointUrl)
        .map(m => `${m.providerModelId}::${m.customEndpointUrl}`)
    );
  }, [existingModels]);

  const duplicateRows = useMemo(() => {
    const result = new Set<number>();
    parsedModels.forEach((m, i) => {
      if (m.providerModelId && m.customEndpointUrl) {
        if (existingModelKeys.has(`${m.providerModelId}::${m.customEndpointUrl}`)) {
          result.add(i);
        }
      }
    });
    return result;
  }, [parsedModels, existingModelKeys]);

  const hasIssues = conflicts.size > 0 || missingFields.size > 0;

  const handleFileChange = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    setParseError('');
    setHasParsed(false);
    setParsedModels([]);

    try {
      const text = await file.text();
      const { rows, error } = parseCSVText(text);
      if (error) { setParseError(error); return; }
      if (rows.length === 0) { setParseError('No data rows found in CSV'); return; }
      setParsedModels(rows.map(rowToModel));
      setHasParsed(true);
    } catch (err: any) {
      setParseError(err.message || 'Failed to read file');
    }
  }, []);

  const handleRankChange = useCallback((index: number, value: string) => {
    const trimmed = value.trim();
    const rank = trimmed === '' ? undefined : parseInt(trimmed, 10);
    setParsedModels(prev => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        ranked_position: rank !== undefined && isNaN(rank) ? undefined : rank,
      };
      return updated;
    });
  }, []);

  const handleDeleteRow = useCallback((index: number) => {
    setParsedModels(prev => prev.filter((_, i) => i !== index));
  }, []);

  const handleAutoAssign = useCallback(() => {
    setParsedModels(prev => {
      // Recompute conflicts from the current state (not the stale memo)
      const conflictingRows = new Set<number>();
      const seenInBatch = new Map<number, number>();

      prev.forEach((model, i) => {
        const rank = model.ranked_position;
        if (rank == null) return;
        if (existingRanks.has(rank)) {
          conflictingRows.add(i);
        } else if (seenInBatch.has(rank)) {
          conflictingRows.add(i);
          conflictingRows.add(seenInBatch.get(rank)!);
        } else {
          seenInBatch.set(rank, i);
        }
      });

      // Seed used ranks with existing models + all non-conflicting CSV rows
      const usedRanks = new Set(existingRanks);
      prev.forEach((m, i) => {
        if (!conflictingRows.has(i) && m.ranked_position != null) {
          usedRanks.add(m.ranked_position);
        }
      });

      const nextFree = () => {
        let rank = 1;
        while (usedRanks.has(rank)) rank++;
        usedRanks.add(rank);
        return rank;
      };

      const updated = [...prev];
      prev.forEach((_, i) => {
        if (conflictingRows.has(i)) {
          updated[i] = { ...updated[i], ranked_position: nextFree() };
        }
      });
      return updated;
    });
  }, [existingRanks]);

  const handleImport = async () => {
    setImporting(true);
    const errors: { row: number; message: string }[] = [];
    let imported = 0;

    for (let i = 0; i < parsedModels.length; i++) {
      const m = parsedModels[i];
      try {
        await apiService.post('/portal/models', {
          id: `${m.providerModelId}-${Date.now()}-${i}`,
          name: m.name,
          providerModelId: m.providerModelId,
          provider: 'custom',
          isCustomEndpoint: true,
          customEndpointUrl: m.customEndpointUrl,
          customApiKey: m.customApiKey,
          contextWindow: m.contextWindow,
          maxTokens: m.maxTokens,
          supports_vision: m.supports_vision,
          supports_streaming: m.supports_streaming,
          taskDeterminationModel: m.taskDeterminationModel,
          isPrivacyCompliant: m.isPrivacyCompliant,
          functionCalling: m.functionCalling,
          isUncensored: m.isUncensored,
          category: m.category || undefined,
          ranked_position: m.ranked_position,
          price_per_token: isAdmin ? m.price_per_token : 'Free',
          capabilities: m.capabilities,
        });
        imported++;
      } catch (err: any) {
        const msg = err.response?.data?.error?.message || err.message || 'Unknown error';
        errors.push({ row: i + 1, message: `${m.name || `Row ${i + 1}`}: ${msg}` });
      }
    }

    setImporting(false);
    onImportComplete(imported, errors);
  };

  const handleClose = () => {
    if (importing) return;
    setParsedModels([]);
    setParseError('');
    setHasParsed(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed top-0 left-0 right-0 bottom-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] !m-0 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-6xl max-h-[95vh] overflow-hidden flex flex-col relative z-[10000]" onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Import Models from CSV</h2>
            {hasParsed && (
              <p className="text-sm text-gray-500 mt-1">
                {parsedModels.length} model{parsedModels.length !== 1 ? 's' : ''} parsed
                {conflicts.size > 0 && (
                  <span className="ml-2 text-red-600 font-medium">
                    · {conflicts.size} rank conflict{conflicts.size !== 1 ? 's' : ''}
                  </span>
                )}
                {missingFields.size > 0 && (
                  <span className="ml-2 text-yellow-600 font-medium">
                    · {missingFields.size} row{missingFields.size !== 1 ? 's' : ''} missing required fields
                  </span>
                )}
                {duplicateRows.size > 0 && (
                  <span className="ml-2 text-amber-600 font-medium">
                    · {duplicateRows.size} already exist
                  </span>
                )}
              </p>
            )}
          </div>
          <button onClick={handleClose} disabled={importing} className="text-gray-400 hover:text-gray-600 disabled:opacity-40">
            <X size={24} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-4 min-h-0">

          {/* File picker — shown before a file is parsed */}
          {!hasParsed && (
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-10 text-center">
              <FileText className="mx-auto h-12 w-12 text-gray-400 mb-3" />
              <p className="text-sm text-gray-600 mb-1">
                Select a CSV file to preview all models before importing.
              </p>
              <p className="text-xs text-gray-400 mb-5">
                Rank conflicts are checked locally — nothing is sent until you confirm.
              </p>
              <label className="cursor-pointer bg-blue-600 text-white px-5 py-2 rounded-md hover:bg-blue-700 text-sm font-medium">
                Choose CSV File
                <input type="file" accept=".csv" onChange={handleFileChange} className="hidden" />
              </label>
              {parseError && (
                <p className="mt-4 text-sm text-red-600 bg-red-50 rounded-md px-4 py-2 inline-block">{parseError}</p>
              )}
            </div>
          )}

          {/* Replace-file link + auto-assign + conflict banner — shown after parsing */}
          {hasParsed && (
            <div className="flex items-center justify-between">
              <label className="cursor-pointer text-sm text-blue-600 hover:text-blue-800 underline">
                Choose a different file
                <input type="file" accept=".csv" onChange={handleFileChange} className="hidden" />
              </label>
              {hasIssues && (
                <div className="flex items-center gap-2 text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 text-sm">
                  <AlertTriangle size={15} />
                  Edit the <strong className="mx-1">Rank</strong> column below to resolve conflicts, then import
                </div>
              )}
            </div>
          )}

          {/* Preview table */}
          {hasParsed && parsedModels.length > 0 && (
            <div className="overflow-x-auto border rounded-lg max-h-[55vh] overflow-y-auto">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50 sticky top-0 z-10">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">#</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Model ID</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Endpoint URL</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                      <div className="flex flex-col gap-1">
                        <span>
                          Rank
                          <span className="ml-1 normal-case font-normal text-blue-500">(editable)</span>
                        </span>
                        {conflicts.size > 0 && (
                          <div className="flex items-center gap-1">
                            <button
                              onClick={handleAutoAssign}
                              className="normal-case font-medium text-xs text-purple-700 bg-purple-50 border border-purple-200 hover:bg-purple-100 px-2 py-0.5 rounded whitespace-nowrap"
                            >
                              Auto Assign
                            </button>
                            <div className="relative group">
                              <Info size={12} className="text-gray-400 hover:text-gray-600 cursor-help" />
                              <div className="absolute left-4 top-0 z-20 hidden group-hover:block w-56 p-2 bg-gray-900 text-white text-xs rounded shadow-lg font-normal normal-case">
                                Fills all conflicting ranks with the next available numbers, starting from 1.
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Vision</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Task Det.</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Privacy</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Fn Calling</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase sticky right-0 bg-gray-50 shadow-[-2px_0_5px_-2px_rgba(0,0,0,0.1)]"></th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {parsedModels.map((model, i) => {
                    const conflict = conflicts.get(i);
                    const isDuplicate = duplicateRows.has(i);
                    const missing = missingFields.has(i);
                    const rowClass = conflict ? 'bg-red-50' : missing ? 'bg-yellow-50' : isDuplicate ? 'bg-amber-50' : '';
                    return (
                      <tr key={i} className={rowClass}>
                        <td className="px-3 py-2 text-gray-400 font-mono text-xs">{i + 1}</td>
                        <td className="px-3 py-2 font-medium text-gray-900">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            {model.name || <span className="text-red-500 italic text-xs">missing</span>}
                            {isDuplicate && (
                              <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-700 whitespace-nowrap">
                                <Info size={10} /> already exists
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-3 py-2 text-gray-500 font-mono text-xs max-w-[160px] truncate" title={model.providerModelId}>
                          {model.providerModelId || <span className="text-red-500 italic">missing</span>}
                        </td>
                        <td className="px-3 py-2 text-gray-500 text-xs max-w-[180px] truncate" title={model.customEndpointUrl}>
                          {model.customEndpointUrl || <span className="text-red-500 italic">missing</span>}
                        </td>
                        <td className="px-3 py-2">
                          <div className="flex items-center gap-1.5">
                            <input
                              type="number"
                              min={1}
                              value={model.ranked_position ?? ''}
                              onChange={e => handleRankChange(i, e.target.value)}
                              className={`w-16 px-2 py-1 border rounded text-sm focus:outline-none focus:ring-2 ${
                                conflict
                                  ? 'border-red-400 bg-red-50 text-red-700 focus:ring-red-300'
                                  : 'border-gray-300 focus:ring-blue-300'
                              }`}
                              placeholder="—"
                            />
                            {conflict && (
                              <span className="text-xs text-red-600 font-medium whitespace-nowrap">
                                {conflict === 'existing' ? '← taken' : '← duplicate'}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-3 py-2 text-gray-500 text-xs">{model.category || '—'}</td>
                        <td className="px-3 py-2 text-center">
                          <span className={`px-1.5 py-0.5 rounded text-xs ${model.supports_vision ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-400'}`}>
                            {model.supports_vision ? '✓' : '✗'}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-center">
                          <span className={`px-1.5 py-0.5 rounded text-xs ${model.taskDeterminationModel ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-400'}`}>
                            {model.taskDeterminationModel ? '✓' : '✗'}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-center">
                          <span className={`px-1.5 py-0.5 rounded text-xs ${model.isPrivacyCompliant ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-400'}`}>
                            {model.isPrivacyCompliant ? '✓' : '✗'}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-center">
                          <span className={`px-1.5 py-0.5 rounded text-xs ${model.functionCalling ? 'bg-indigo-100 text-indigo-700' : 'bg-gray-100 text-gray-400'}`}>
                            {model.functionCalling ? '✓' : '✗'}
                          </span>
                        </td>
                        <td className={`px-3 py-2 text-center sticky right-0 shadow-[-2px_0_5px_-2px_rgba(0,0,0,0.1)] ${rowClass || 'bg-white'}`}>
                          <button
                            onClick={() => handleDeleteRow(i)}
                            className="text-gray-400 hover:text-red-600 transition-colors"
                            title="Remove this row"
                          >
                            <Trash2 size={14} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Footer */}
        {hasParsed && (
          <div className="flex items-center justify-between px-6 py-4 bg-gray-50 border-t rounded-b-xl">
            <div className="text-sm">
              <div className="flex flex-col gap-1">
                {hasIssues ? (
                  <span className="flex items-center gap-1.5 text-red-600">
                    <AlertTriangle size={14} />
                    {conflicts.size > 0 && `${conflicts.size} rank conflict${conflicts.size !== 1 ? 's' : ''}`}
                    {conflicts.size > 0 && missingFields.size > 0 && ' · '}
                    {missingFields.size > 0 && `${missingFields.size} row${missingFields.size !== 1 ? 's' : ''} missing required fields`}
                  </span>
                ) : (
                  <span className="flex items-center gap-1.5 text-green-600">
                    <CheckCircle2 size={14} />
                    Ready to import {parsedModels.length} model{parsedModels.length !== 1 ? 's' : ''}
                  </span>
                )}
                {duplicateRows.size > 0 && (
                  <span className="flex items-center gap-1.5 text-amber-600 text-xs">
                    <Info size={12} />
                    {duplicateRows.size} row{duplicateRows.size !== 1 ? 's' : ''} already exist and will be added as duplicates — delete them above to skip
                  </span>
                )}
              </div>
            </div>
            <div className="flex gap-3">
              <button
                onClick={handleClose}
                disabled={importing}
                className="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleImport}
                disabled={hasIssues || importing}
                className={`px-4 py-2 text-sm text-white rounded-md font-medium ${
                  hasIssues || importing ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'
                }`}
              >
                {importing
                  ? 'Importing...'
                  : `Import ${parsedModels.length} Model${parsedModels.length !== 1 ? 's' : ''}`}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
